import { useCallback, useEffect, useState } from "react";
import { Eraser, Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Calendar dates (YYYY-MM-DD) render at noon to avoid timezone day-shifts.
const day = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "");
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Toronto" }) : "");

function NextClear({ row }) {
  if (!row.enabled) {
    return (
      <>
        <p className="font-semibold text-[#777]">Off: nothing will be cleared</p>
        {row.next_play_date && <p className="text-xs text-[#999]">Next play date {day(row.next_play_date)}</p>}
      </>
    );
  }
  if (!row.next_clear_date) {
    return <p className="font-semibold text-amber-700">{row.has_schedule_dates ? "No upcoming play dates in the schedule" : "No dates found in the schedule"}</p>;
  }
  return (
    <>
      <p className="font-semibold text-[#111]">{day(row.next_clear_date)} · 12:00 am</p>
      <p className="text-xs text-[#555]">after the {day(row.next_play_date)} play date</p>
    </>
  );
}

function Row({ row, saving, onToggle }) {
  const t = row.slug;
  return (
    <div className="grid gap-3 py-3.5 sm:grid-cols-[minmax(0,1.3fr)_auto_minmax(0,1.2fr)_minmax(0,1.2fr)] sm:items-center" data-testid={`auto-clear-row-${t}`}>
      <div className="min-w-0">
        <p className="font-semibold truncate">{row.name}</p>
        {row.night && <p className="text-xs text-[#777] truncate">{row.night}</p>}
        {!row.highlights_enabled && (
          <p className="text-xs text-amber-700 inline-flex items-center gap-1 mt-0.5"><AlertTriangle className="h-3 w-3" /> Weekly highlights are off</p>
        )}
      </div>
      <label className="inline-flex items-center gap-2.5 cursor-pointer sm:w-[92px]">
        <Switch checked={row.enabled} disabled={saving} onCheckedChange={(v) => onToggle(row, v)} data-testid={`auto-clear-toggle-${t}`} aria-label={`Auto-clear for ${row.name}`} />
        <span className={`text-xs font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${row.enabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`} data-testid={`auto-clear-status-${t}`}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin inline" /> : row.enabled ? "On" : "Off"}
        </span>
      </label>
      <div className="text-sm" data-testid={`auto-clear-next-${t}`}>
        <p className="text-[11px] uppercase tracking-wide font-semibold text-[#999] sm:hidden">Next clear</p>
        <NextClear row={row} />
      </div>
      <div className="text-sm" data-testid={`auto-clear-last-${t}`}>
        <p className="text-[11px] uppercase tracking-wide font-semibold text-[#999] sm:hidden">Last cleared</p>
        {row.last_cleared_at ? (
          <>
            <p className="font-medium text-[#333]">{when(row.last_cleared_at)}</p>
            {row.last_cleared_for && <p className="text-xs text-[#555]">after the {day(row.last_cleared_for)} play date</p>}
          </>
        ) : <p className="text-[#999]">Not yet</p>}
      </div>
    </div>
  );
}

export function AutoClearPanel({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/divisions/auto-clear")
      .then((r) => setRows(r.data.divisions))
      .catch((e) => { setRows((rs) => rs || []); toast.error(formatApiError(e)); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const toggle = async (row, enabled) => {
    setSaving(row.id);
    try {
      const { data } = await api.put(`/admin/divisions/${row.id}/auto-clear`, { enabled });
      setRows((rs) => rs.map((r) => (r.id === row.id ? data : r)));
      toast.success(`Auto-clear ${enabled ? "on" : "off"} for ${row.name}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving("");
    }
  };

  return (
    <section className="bg-white rounded-lg border border-[#E5E7EB] p-5" data-testid="auto-clear-panel">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 shrink-0 rounded-md bg-[#1E63C4]/10 text-[#1E63C4] flex items-center justify-center"><Eraser className="h-5 w-5" /></span>
          <div>
            <h3 className="font-heading font-bold text-lg leading-tight">Auto-clear weekly highlights</h3>
            <p className="text-sm text-[#555] mt-1 max-w-3xl">
              At 12:00 am (Toronto time) after each play date in a division&apos;s schedule, these are cleared for that division only: WANTED poster,
              WANTED Player and Wanted players list, Player Improvement list, Live Stream Match poster and team names. Team names and WANTED Player show &quot;Coming Soon&quot;.
              Turning a division on starts from its next play date.
            </p>
          </div>
        </div>
        <button type="button" onClick={load} disabled={loading} aria-label="Refresh auto-clear status" data-testid="auto-clear-refresh"
          className="shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-md border border-[#E5E7EB] text-[#555] hover:text-[#1E63C4] hover:border-[#1E63C4] transition-colors disabled:opacity-60">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      <div className="mt-4 border-t border-[#E5E7EB]">
        <div className="hidden sm:grid grid-cols-[minmax(0,1.3fr)_auto_minmax(0,1.2fr)_minmax(0,1.2fr)] gap-3 pt-3 text-[11px] uppercase tracking-wide font-semibold text-[#999]">
          <span>Division</span><span className="w-[92px]">Auto-clear</span><span>Next clear</span><span>Last cleared</span>
        </div>
        {rows === null ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#1E63C4]" /></div>
        ) : rows.length === 0 ? (
          <p className="py-4 text-sm text-[#777]">No current divisions.</p>
        ) : (
          <div className="divide-y divide-[#E5E7EB]">
            {rows.map((r) => <Row key={r.id} row={r} saving={saving === r.id} onToggle={toggle} />)}
          </div>
        )}
      </div>
    </section>
  );
}
